import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { useTenantCatalog } from '../hooks';
import { CreateIssueModal } from '../components/CreateIssueModal';
import { IssueList } from '../components/IssueList';
import { Icon } from '../shared/Icon';
import type { Issue, Page } from '../types';

/**
 * Задачи одной очереди — рабочий экран трекера. Очередь уже выбрана, поэтому фильтра по очередям
 * здесь нет, а новая задача сразу заводится в ней: выбирать очередь второй раз незачем.
 */
export function QueuePage() {
  const { tenantId = '', queueId = '' } = useParams();
  const navigate = useNavigate();
  const catalog = useTenantCatalog(tenantId);
  const [text, setText] = useState('');
  const [statusId, setStatusId] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [creating, setCreating] = useState(false);

  const queue = catalog.queueById.get(queueId);

  const criteria = {
    text: text || undefined,
    queueIds: [queueId],
    statusIds: statusId ? [statusId] : undefined,
    resolved: onlyOpen ? false : undefined,
  };

  const issues = useQuery({
    queryKey: ['issues', tenantId, criteria],
    queryFn: () =>
      api.post<Page<Issue>>(`/tenants/${tenantId}/issues/search?size=50&sort=created,desc`, criteria),
    enabled: Boolean(queueId),
  });

  if (catalog.isLoading) return <div className="state">Загрузка…</div>;

  // Очередь могли удалить или ссылка ведёт в чужой тенант — это не ошибка сервиса.
  if (!queue) {
    return (
      <div className="card">
        <div className="state-title">Очередь не найдена</div>
        <p className="muted">Возможно, её удалили или ссылка ведёт в другой трекер.</p>
        <div className="state-actions">
          <button className="btn btn-primary" onClick={() => navigate(`/t/${tenantId}/queues`)}>
            Ко всем очередям
          </button>
        </div>
      </div>
    );
  }

  const found = issues.data?.totalElements ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="muted small">
            <Link to={`/t/${tenantId}/queues`}>Очереди</Link>
          </div>
          <h1>
            <span className="queue-key" style={{ marginRight: 8, marginBottom: 0 }}>
              {queue.key}
            </span>
            {queue.name}
          </h1>
          <div className="muted small">{issues.data ? `Найдено: ${found}` : ' '}</div>
        </div>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          <Icon name="plus" />
          Новая задача
        </button>
      </div>

      <div className="filters">
        <input
          className="input"
          placeholder="Поиск по ключу, заголовку, описанию"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <select className="input" value={statusId} onChange={(event) => setStatusId(event.target.value)}>
          <option value="">Все статусы</option>
          {catalog.statuses.map((status) => (
            <option key={status.id} value={status.id}>
              {status.name}
            </option>
          ))}
        </select>
        <label>
          <input
            type="checkbox"
            checked={onlyOpen}
            onChange={(event) => setOnlyOpen(event.target.checked)}
          />
          Только незакрытые
        </label>
      </div>

      {issues.isLoading ? (
        <div className="state">Загрузка…</div>
      ) : issues.isError ? (
        <div className="state">{(issues.error as ApiError).message}</div>
      ) : found === 0 ? (
        <div className="card">
          {/* Пусто по фильтру и пусто вообще — разные вещи. Фильтр «только незакрытые» включён по
              умолчанию, поэтому судим по счётчику очереди, а не по набору фильтров. */}
          <div className="state-title">
            {queue.issueCounter === 0 ? 'В очереди пока нет задач' : 'Ничего не найдено'}
          </div>
          <p className="muted">
            {queue.issueCounter === 0
              ? `Заведите первую задачу — она получит ключ ${queue.key}-1.`
              : 'Под текущие условия ничего не подходит: снимите фильтры или измените поиск.'}
          </p>
          <div className="state-actions">
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" />
              Новая задача
            </button>
          </div>
        </div>
      ) : (
        <IssueList
          tenantId={tenantId}
          issues={issues.data!.content}
          statusById={catalog.statusById}
        />
      )}

      {creating && (
        <CreateIssueModal
          tenantId={tenantId}
          queues={catalog.queues}
          fixedQueueId={queue.id}
          onClose={() => setCreating(false)}
          onCreated={(issue) => navigate(`/t/${tenantId}/issues/${issue.key}`)}
        />
      )}
    </>
  );
}
